import { DeleteIcon, EditIcon } from '@carboncopyco/icons';
import { Checkbox, ColorHex } from '@carboncopyco/ui';
import { useBoolean, useDebounceFn, useHover } from 'ahooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ITextReplaceResult } from '../types';
import { PreviewTextItem } from './PreviewTextItem';

export interface DataListItemProps {
  editItemErrorKey?: string;
  setEditItemErrorKey?: (key: string) => void;
  editKey: string;
  setEditKey: (key: string) => void;
  item: ITextReplaceResult;
  onCheck: (isCheck: boolean) => void;
  isChecked: boolean;
  onDelete?: () => void;
  checkOriginalReplaceChangeText: (oldValue: string) => boolean;
  onEditText?: (data: {
    newOriginalText: string;
    newReplaceText: string;
  }) => void;
}

export const DataListItem = ({
  item,
  onCheck,
  isChecked,
  onDelete,
  onEditText,
  editKey,
  setEditKey,
  editItemErrorKey,
  setEditItemErrorKey,
  checkOriginalReplaceChangeText,
}: DataListItemProps) => {
  const ref = useRef(null);
  const lineRef = useRef(null);
  const [isEditMode, setIsEditMode] = useBoolean(false);
  const isHovering = useHover(ref);
  const [newOriginalText, setNewOriginalText] = useState(item.original);
  const [newReplaceText, setNewReplaceText] = useState(item.replace_with);
  const [errorMessageOld, setErrorMessageOld] = useState('');
  const [errorMessageNew, setErrorMessageNew] = useState('');
  const isEditOnClick = !isEditMode && !editItemErrorKey?.length;

  const textEmptyCheck = (
    value: string,
    setError: (message: string) => void
  ) => {
    if (!value) {
      setError(
        'You must enter text. Otherwise, the previous text will be used.'
      );
      return true;
    }
    return false;
  };

  const changeText = (
    value: string,
    setText: (value: string) => void,
    setErrorMessage: (message: string) => void,
    setEditItemErrorKey: (key: string) => void,
    itemKey: string,
    editItemErrorKey: string,
    errorCheck?: (value: string, setError?: (value: string) => void) => boolean
  ) => {
    setText(value);
    if (errorCheck(value, setErrorMessage)) {
      setEditItemErrorKey(itemKey);
    } else {
      setErrorMessage('');
      if (editItemErrorKey === itemKey) {
        setEditItemErrorKey('');
      }
    }
  };

  const valueIsExist = (value: string) => {
    if (checkOriginalReplaceChangeText(value)) {
      setErrorMessageOld('This rule already exists and cannot be used again.');
      return true;
    }
    return false;
  };

  const changeOriginalText = useCallback(
    (value: string) =>
      changeText(
        value,
        setNewOriginalText,
        setErrorMessageOld,
        setEditItemErrorKey,
        item.original,
        editItemErrorKey,
        (value, setError) =>
          textEmptyCheck(value, setError) || valueIsExist(value)
      ),
    [editItemErrorKey, item.original]
  );

  const changeReplaceText = useCallback(
    (value: string) =>
      changeText(
        value,
        setNewReplaceText,
        setErrorMessageNew,
        setEditItemErrorKey,
        item.replace_with,
        editItemErrorKey,
        (value, setError) => textEmptyCheck(value, setError)
      ),
    [editItemErrorKey, item.replace_with]
  );

  useEffect(() => {
    const hasNewChanges =
      newOriginalText !== item.original || newReplaceText !== item.replace_with;
    if (!isEditMode && hasNewChanges) {
      onEditText?.({ newOriginalText, newReplaceText });
    }
    if (isEditMode) {
      setEditKey(item.original);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (editKey !== item.original) {
      setIsEditMode.set(false);
    }
  }, [editKey, item.original]);

  const { run: onClickItem } = useDebounceFn(
    () => {
      if (!isEditOnClick || isEditMode) return;
      setIsEditMode.setTrue();
    },
    {
      wait: 200,
    }
  );

  return (
    <div
      className="flex justify-between gap-3 w-full items-center h-10"
      aria-label="AI Rule Item"
      ref={lineRef}
    >
      <Checkbox
        checked={isChecked}
        name={item.original.toString()}
        onChange={() => onCheck(!isChecked)}
      />
      <div
        onClick={onClickItem}
        className="flex bg-ds-natural-50 rounded-md w-full h-full px-3 py-2.5 w-[520px] grow"
        ref={ref}
      >
        <div className="flex grow w-9/12">
          <PreviewTextItem
            edit={{
              old: { value: item.original },
              new: { value: item.replace_with },
            }}
            editTextOld={newOriginalText}
            setEditTextOld={changeOriginalText}
            errorMessageNew={errorMessageNew}
            errorMessageOld={errorMessageOld}
            editTextNew={newReplaceText}
            setEditTextNew={changeReplaceText}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
          />
        </div>
        <div className=" w-5">
          {isEditMode ? (
            <div className="flex">
              <div onClick={onDelete}>
                <DeleteIcon
                  size={20}
                  color={ColorHex.primary300}
                />
              </div>
            </div>
          ) : (
            <div
              className="flex"
              onClick={() => {
                if (isHovering) setIsEditMode.toggle();
              }}
            >
              {isHovering ? (
                <EditIcon
                  size={20}
                  color={ColorHex.primary300}
                />
              ) : (
                <div />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
